import { reportError } from "../services/Errors";

const BUCKETS = [
  'logictrystatic',
  'logictryuserprofiles',
  'logictryuserfiles',
  'logictrycompanyfiles'
];

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'tiff', 'avif', 'bmp', 'heic'];

const S3_URL_PATTERN = new RegExp(
  `https?:\\/\\/(?:(?:(${BUCKETS.join('|')})\\.s3(?:[.-][a-z0-9-]+)?\\.amazonaws\\.com)|(?:s3(?:[.-][a-z0-9-]+)?\\.amazonaws\\.com\\/(${BUCKETS.join('|')})))(?:\\/)?(.+)$`,
  'i'
);

/**
 * @returns {{ bucket: string, key: string } | null}
 */
function matchLogictryS3Url(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(S3_URL_PATTERN);
  if (!m) return null;
  const bucket = m[1] || m[2];
  const key = m[3];
  if (!bucket || !key) return null;
  return { bucket, key: decodeURIComponent(key) };
}

/**
 * Logictry S3 virtual-host URLs → `https://cdn.logictry.com/...`
 * - **logictryuserfiles**: image keys use the resize base64 pipeline (optional width/height/format). Non-image keys: same path/query/hash with origin `https://cdn.logictry.com` (web apps, PDF, video, etc.).
 * - **Other buckets**: only image keys are converted; everything else is returned unchanged.
 */
export default function convertUrlfromS3toCloudFront(url, width, height, format) {
  if (!url || typeof url !== 'string') return url;

  try {
    const parsed = matchLogictryS3Url(url);
    if (!parsed) return url;

    const ext = parsed.key.split('.').pop().toLowerCase();
    const isImage = IMAGE_EXTENSIONS.includes(ext);

    if (parsed.bucket === 'logictryuserfiles') {
      if (isImage) {
        return generateImageUrl({
          cdnBase: 'https://cdn.logictry.com',
          bucket: parsed.bucket,
          key: parsed.key,
          width,
          height,
          format
        });
      }
      return rewriteUserfilesS3OriginToCdn(url);
    }

    if (!isImage) return url;

    return generateImageUrl({
      cdnBase: 'https://cdn.logictry.com',
      bucket: parsed.bucket,
      key: parsed.key,
      width,
      height,
      format
    });
  } catch (err) {
    const error = new Error(`S3 to CloudFront URL Conversion Failed: ${url}

More details: ${err?.message || err || ''}`);
    reportError(error.message, error.stack);
    return url;
  }
}

function rewriteUserfilesS3OriginToCdn(url) {
  try {
    const u = new URL(url);
    if (u.hostname === 'cdn.logictry.com') return url;
    return `https://cdn.logictry.com${u.pathname}${u.search}${u.hash}`;
  } catch {
    return url;
  }
}

function generateImageUrl({ cdnBase, bucket, key, width, height, format }) {
  const edits = {};
  if (width || height) edits.resize = { width, height, fit: "cover" };
  if (format) edits.format = format;
  const payload = { edits, bucket, key };
  const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload)))); // base64 encode
  const fullurl = `${cdnBase.replace(/\/$/, '')}/${encoded}`;
  return fullurl;
}
